import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { RootState } from '@/redux/store';
import { SATLinkStatus } from '@/@types/SATLinking';
import { setTaxpayer } from '@/redux/slices/taxProfile.slice';
import { getTaxpayerService } from '@/shared/services/fiscal.service';
import { getSyncStatus } from './syncStatus.service';
import type { SyncStatusResponse } from './syncStatus.types';

export const SYNC_STATUS_QUERY_KEY = ['sync-status'] as const;

const REFRESH_TRIGGERS = ['TAX_STATUS_CERTIFICATE', 'COMPLIANCE'] as const;

function findExtractionStatus(data: SyncStatusResponse | null, type: string): string | undefined {
  if (!data?.waves) return undefined;
  const all = [
    ...(data.waves.wave_1?.extractions ?? []),
    ...(data.waves.wave_2?.extractions ?? []),
    ...(data.waves.wave_3?.extractions ?? []),
  ];
  return all.find(e => e.type === type)?.status;
}

export const useSyncStatus = () => {
  const taxpayer = useSelector((state: RootState) => state.taxProfile.taxpayer);
  const isLinked = taxpayer?.link_status === SATLinkStatus.ACTIVE;
  const dispatch = useDispatch();
  const refreshedFor = useRef<Set<string>>(new Set());

  const query = useQuery<SyncStatusResponse | null>({
    queryKey: SYNC_STATUS_QUERY_KEY,
    queryFn: () => (isLinked ? getSyncStatus() : null),
    enabled: isLinked,
    staleTime: 0,
    refetchInterval: (queryState) => {
      const hint = queryState.state.data?.polling_hint;
      if (!hint || hint.should_stop) return false;
      return hint.recommended_interval_ms;
    },
  });

  const data = query.data ?? null;

  useEffect(() => {
    if (!data?.waves) return;
    const needsRefresh = REFRESH_TRIGGERS.some(type => {
      const status = findExtractionStatus(data, type);
      return status === 'completed' && !refreshedFor.current.has(type);
    });

    if (needsRefresh) {
      REFRESH_TRIGGERS.forEach(type => {
        if (findExtractionStatus(data, type) === 'completed') {
          refreshedFor.current.add(type);
        }
      });
      getTaxpayerService()
        .then((response) => dispatch(setTaxpayer(response)))
        .catch(() => {});
    }
  }, [data, dispatch]);

  const isExtractionCompleted = (type: string) =>
    findExtractionStatus(data, type) === 'completed';

  // `sync_id: null` only comes from the service's 404 fallback: there is no
  // execution to wait for. The Go engine doesn't send `sync_id` at all.
  const noExecution = data?.sync_id === null;
  /**
   * THE "Sincronizando tu información" of the web: true exactly while
   * `SyncStatusWidget` shows its bar. A `failed` sync also comes with
   * `is_complete: false` (fiscal-engine marks it so after 6 h stuck): it is not
   * syncing, nothing is coming. Everything that has to wait for the SAT
   * sync (Regularización total included, via `useMeasuredOffer`) reads this,
   * so nothing shows up while the bar says it's still syncing.
   */
  const isSyncing =
    !query.isLoading &&
    !query.isError &&
    !!data &&
    !noExecution &&
    !data.is_complete &&
    data.overall_status !== 'failed';

  return {
    data,
    isLoading: query.isLoading,
    isError: query.isError,
    isSyncing,
    hasUsefulData: data?.has_useful_data ?? false,
    isComplete: data?.is_complete ?? false,
    overallProgress: data?.overall_progress ?? 0,
    groups: data?.ui_groups ?? null,
    noExecution,
    isExtractionCompleted,
  };
};
